import React, { useState, useEffect, useRef } from "react";
import styles from "./InvoiceActivity.module.scss";
import classNames from "classnames/bind";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { PlusIcon } from "~/assets/icons";
import dayjs from "dayjs";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";
import { ArrowDown, SearchIcon } from "lucide-react";

const cx = classNames.bind(styles);

export interface IInvoiceActivity {
  postedOnDate: string;
  postedBy: string;
  text: string;
}

interface IInvoiceActivityProps {
  invoiceActivity: IInvoiceActivity[] | null;
  onAddNote?: () => void;
}

const FILTER_OPTIONS = [
  {
    label: "All",
    value: "all",
  },
];

const InvoiceActivity = (props: IInvoiceActivityProps) => {
  const { invoiceActivity, onAddNote } = props;
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showItemsDropdown, setShowItemsDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [filterType, setFilterType] = useState("all");
  const itemsPerPageOptions = [10, 20, 50, 100];

  const filteredActivities = React.useMemo(() => {
    if (!invoiceActivity) return [];
    
    if (!searchQuery.trim()) {
      return invoiceActivity;
    }

    const query = searchQuery.toLowerCase().trim();
    return invoiceActivity.filter((activity) => {
      const textMatch = activity.text?.toLowerCase().includes(query);
      const postedByMatch = activity.postedBy?.toLowerCase().includes(query);
      const dateMatch = dayjs(activity.postedOnDate)
        .format("MM/DD/YYYY hh:mm A")
        .toLowerCase()
        .includes(query);
      
      return textMatch || postedByMatch || dateMatch;
    });
  }, [invoiceActivity, searchQuery]);

  const totalItems = filteredActivities.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  React.useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showItemsDropdown) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowItemsDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showItemsDropdown]);

  const handleChangePage = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setPage(1);
    setShowItemsDropdown(false);
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxButtons = 5;

    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 3) {
        pages.push(1, 2, 3, "...", totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", page - 1, page, page + 1, "...", totalPages);
      }
    }

    return pages.filter((p, idx, arr) => !(p === arr[idx - 1]));
  };

  const paginatedData = React.useMemo(() => {
    return filteredActivities.slice(
      (page - 1) * itemsPerPage,
      page * itemsPerPage
    );
  }, [filteredActivities, page, itemsPerPage]);

  return (
    <div className={cx("invoice-activity-container")}>
      <div className={cx("header")}>
        <h3 className={cx("title")}>Activity</h3>

        <div className={cx("actions")}>
          <PrimarySelectInput
            name="invoiceActivity"
            options={FILTER_OPTIONS}
            placeholder="Select Filter"
            value={FILTER_OPTIONS.find((option) => option.value === filterType)?.label || ""}
            onChange={(value) => setFilterType(value)}
            className={cx("select-input")}
          />

          <div className={cx("search-container")}>
            <SearchIcon className={cx("search-icon")} />
            <input
              type="text"
              placeholder="Search"
              className={cx("search-input")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <PrimaryButton
            title="Add note"
            icon={<PlusIcon className={cx("plus-icon")} />}
            onClick={() => {
              if (onAddNote) {
                onAddNote();
              }
            }}
            className={cx("add-activity-button")}
          />
        </div>
      </div>
      <div className={cx("sections-container")}>
        <div className={cx("table-wrapper")}>
          <table className={cx("sections-table")}>
            <thead>
              <tr>
                <th className={cx("col-posted-on-date")}>Posted on</th>
                <th className={cx("col-text")}>Text</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((activity, activityIndex) => (
                <tr key={activityIndex} className={cx("item-row")}>
                  <td className={cx("col-posted-on-date")}>
                    {dayjs(activity.postedOnDate).format("MM/DD/YYYY hh:mm A")}
                    <span className={cx("posted-by")}>{activity.postedBy}</span>
                  </td>
                  <td className={cx("col-text")}>{activity.text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={cx("pagination-container")}>
        <div className={cx("pagination-left")}>
          <span className={cx("pagination-text")}>Show result</span>
          <div className={cx("items-per-page-wrapper")} ref={dropdownRef}>
            <button
              className={cx("items-per-page-button")}
              onClick={() => setShowItemsDropdown(!showItemsDropdown)}
            >
              <span>{itemsPerPage}</span>
              <svg
                width="9"
                height="9"
                viewBox="0 0 9 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.243 5.656L0 1.414L1.415 0L4.243 2.828L7.071 0L8.486 1.414L4.243 5.656Z"
                  fill="#789EB4"
                />
              </svg>
            </button>
            {showItemsDropdown && (
              <div className={cx("items-per-page-dropdown")}>
                {itemsPerPageOptions.map((option) => (
                  <button
                    key={option}
                    className={cx("dropdown-item", {
                      active: itemsPerPage === option,
                    })}
                    onClick={() => handleItemsPerPageChange(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>
          <span className={cx("pagination-text")}>out of</span>
          <span className={cx("pagination-total")}>{totalItems}</span>
        </div>

        {/* Right side: Page navigation */}
        <div className={cx("pagination-right")}>
          <button
            className={cx("pagination-nav-button", {
              disabled: page === 1 || page === 0,
            })}
            onClick={() => handleChangePage(page - 1)}
            disabled={page === 1 || page === 0}
          >
            <ArrowDown className={cx("nav-icon", "rotate-90")} />
            <span>Previous</span>
          </button>

          <div className={cx("page-numbers")}>
            {getPageNumbers().map((pageNum, index) => {
              if (pageNum === "...") {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className={cx("page-ellipsis")}
                  >
                    ...
                  </span>
                );
              }
              const pageNumber = Number(pageNum);
              const isActive = page === pageNumber;
              return (
                <button
                  key={pageNumber}
                  className={cx("page-number", {
                    active: isActive,
                  })}
                  onClick={() => handleChangePage(pageNumber)}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>

          <button
            className={cx("pagination-nav-button", {
              disabled: page === totalPages,
            })}
            onClick={() => handleChangePage(page + 1)}
            disabled={page === totalPages}
          >
            <span>Next</span>
            <ArrowDown className={cx("nav-icon", "!rotate-[270deg]")} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceActivity;
